import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import XRegExp from "xregexp";
import { RfrStepProps } from "../../../pages/FileRFR/RfRForm";
import AmpButton from "../../common/AmpButton/AmpButton";
import AmpFormError from "../../common/AmpFormError/AmpFormError";
import RfrInput from "./RfrInput";
import RfrInputName from "./RfrInputName";
import RfrInputPhone from "./RfrInputPhone";
import RfrUploadFiles from "./RfrUploadFiles";

import "./Steps.css";

export default function Step1(props: RfrStepProps) {
    const navigate = useNavigate();
    const { t } = useTranslation("rfr");
    const formdata = props.formdata;
    const [stepdata, setStepdata] = useState({
        step11OwnerInput: "step11OwnerInput" in formdata ? formdata["step11OwnerInput"] : "",
        step11RepTypeInput: "step11RepTypeInput" in formdata ? formdata["step11RepTypeInput"] : "",
        step11RepNameInputFirst: "step11RepNameInputFirst" in formdata ? formdata["step11RepNameInputFirst"] : "",
        step11RepNameInputLast: "step11RepNameInputLast" in formdata ? formdata["step11RepNameInputLast"] : "",
        step11RepLicenceInput: "step11RepLicenceInput" in formdata ? formdata["step11RepLicenceInput"] : "",
        step11RepPhoneInput: "step11RepPhoneInput" in formdata ? formdata["step11RepPhoneInput"] : "",
        step11RepPhoneInputExt: "step11RepPhoneInputExt" in formdata ? formdata["step11RepPhoneInputExt"] : "",
        step11RepCompanyInput: "step11RepCompanyInput" in formdata ? formdata["step11RepCompanyInput"] : "",
        step11RepPositionInput: "step11RepPositionInput" in formdata ? formdata["step11RepPositionInput"] : "",
        step11RepOtherNameInputFirst: "step11RepOtherNameInputFirst" in formdata ? formdata["step11RepOtherNameInputFirst"] : "",
        step11RepOtherNameInputLast: "step11RepOtherNameInputLast" in formdata ? formdata["step11RepOtherNameInputLast"] : "",
        step11RepOtherPhoneInput: "step11RepOtherPhoneInput" in formdata ? formdata["step11RepOtherPhoneInput"] : "",
        step11RepOtherPhoneInputExt: "step11RepOtherPhoneInputExt" in formdata ? formdata["step11RepOtherPhoneInputExt"] : "",
        step11RepOtherCompanyInput: "step11RepOtherCompanyInput" in formdata ? formdata["step11RepOtherCompanyInput"] : "",
        step11RepOtherPositionInput: "step11RepOtherPositionInput" in formdata ? formdata["step11RepOtherPositionInput"] : "",
    });
    const [files, setFiles] = useState("step11RepOtherFileInput" in formdata ? formdata["step11RepOtherFileInput"] : []);
    const [errors, setErrors] = useState({
        step11OwnerInput: "",
        step11RepTypeInput: "",
        step11RepNameInput: "",
        step11RepLicenceInput: "",
        step11RepPhoneInput: "",
        step11RepOtherNameInput: "",
        step11RepOtherPhoneInput: "",
        step11RepOtherFileInput: ""
    });

    const clearErrors = () => {
        Object.keys(errors).forEach((err) => {
            errors[err] = "";
        });
        setErrors(prevState => ({
            ...prevState,
            ...errors
        }));
    }

    const clearStepdata = (e) => {
        e.preventDefault();
        clearErrors();
        Object.keys(stepdata).forEach((key) => {
            stepdata[key] = "";
        });
        setStepdata(prevState => ({
            ...prevState,
            ...stepdata
        }));
        setFiles([]);
    }

    const trimData = () => {
        Object.keys(stepdata).forEach((key) => {
            const value = stepdata[key];
            if (typeof value === "string") {
                stepdata[key] = value.trim();
            }
        });
        setStepdata(prevState => ({
            ...prevState,
            ...stepdata
        }));
    }

    const setData = (name: string, value: string) => {
        stepdata[name] = value;
        setStepdata(prevState => ({
            ...prevState,
            ...stepdata
        }));
    }

    const handleInputData = (event) => {
        const { name, value } = event.target;
        setData(name, value);
    }

    const handleCheckboxInput = (event) => {
        const { name, value } = event.target;
        if (stepdata[name] !== value) {
            clearErrors();
            setData(name, value);
            if (name === "step11OwnerInput" && value === "YES") {
                setData("step11RepTypeInput", "");
            }
        }
    }

    const validate = () => {
        clearErrors();
        trimData();

        let valid = true;
        if (stepdata["step11OwnerInput"] === "") {
            valid = false;
            errors["step11OwnerInput"] = t("rfr.rfrform.step1.error-owner");
        } else if (stepdata["step11OwnerInput"] === "NO") {
            // /^[\w\u00C0-\u00FF\d]([.',-]?[\w\u00C0-\u00FF\d ])*([.\w\u00C0-\u00FF\d])?$/
            const regexp = XRegExp("^[\\p{L}\\p{N}]([.',-]?[\\p{L}\\p{N} ])*([.\\p{L}\\p{N}])?$", "i");
            if (stepdata["step11RepTypeInput"] === "") {
                valid = false;
                errors["step11RepTypeInput"] = t("rfr.rfrform.step1.error-rep");
            } else if (stepdata["step11RepTypeInput"] === "repLegal") {
                if (stepdata["step11RepNameInputFirst"] === "" || stepdata["step11RepNameInputLast"] === "") {
                    valid = false;
                    errors["step11RepNameInput"] = t("rfr.rfrform.error-name");
                } else {
                    if (regexp.test(stepdata["step11RepNameInputFirst"]) === false || regexp.test(stepdata["step11RepNameInputLast"]) === false) {
                        valid = false;
                        errors["step11RepNameInput"] = t("rfr.rfrform.error-name");
                    }
                }
                if (stepdata["step11RepLicenceInput"] === "") {
                    valid = false;
                    errors["step11RepLicenceInput"] = t("rfr.rfrform.step1.error-license");
                }
                const phone = stepdata["step11RepPhoneInput"];
                if (phone === "" || phone.length < 10) {
                    valid = false;
                    errors["step11RepPhoneInput"] = t("rfr.rfrform.error-phone");
                }

            } else if (stepdata["step11RepTypeInput"] === "repOther") {
                if (stepdata["step11RepOtherNameInputFirst"] === "" || stepdata["step11RepOtherNameInputLast"] === "") {
                    valid = false;
                    errors["step11RepOtherNameInput"] = t("rfr.rfrform.error-name");
                } else {
                    if (regexp.test(stepdata["step11RepOtherNameInputFirst"]) === false || regexp.test(stepdata["step11RepOtherNameInputLast"]) === false) {
                        valid = false;
                        errors["step11RepOtherNameInput"] = t("rfr.rfrform.error-name");
                    }
                }
                const phone = stepdata["step11RepOtherPhoneInput"];
                if (phone === "" || phone.length < 10) {
                    valid = false;
                    errors["step11RepOtherPhoneInput"] = t("rfr.rfrform.error-phone");
                }
                if (files.length === 0) {
                    valid = false;
                    errors["step11RepOtherFileInput"] = t("rfr.rfrform.step1.error-files");
                }
            }
        }
        setErrors(errors);
        return valid;
    }

    const handleCancel = (e) => {
        e.preventDefault();
        navigate("/amp/rfr", { replace: true });
    }

    const handleNext = (e) => {
        const valid = validate();
        if (!valid) {
            const firstError = findFirstError();
            if (firstError !== null && firstError !== undefined) {
                firstError.scrollIntoView({ behavior: "smooth", block: "start" });
            }
            e.preventDefault();
            return;
        }
        props.handleStepChange("step2", stepdata, files, "step11RepOtherFileInput");
    }

    const findFirstError = () => {
        let firstError = null;
        Object.keys(errors).forEach((err) => {
            if (errors[err] !== "" && firstError === null) {
                let elems = document.getElementsByName(err);
                if (elems === null || elems === undefined || elems.length === 0) {
                    elems = document.getElementsByName(err + "First");
                }
                if (elems !== null && elems !== undefined && elems.length > 0) {
                    firstError = elems[0];
                }
            }
        });
        return firstError;
    }

    return (
        <div>
            <div className="tab-title font_size_13">
                {t("rfr.rfrform.step1.tab-title")}
            </div>
            <div className="form_body">
                <div className="rfr-form-blue-row">
                    {t("rfr.rfrform.step1.description")}
                </div>

                <div className="rfr-form-description" id="ownerDiv">
                    <p className="font-blue">{t("rfr.rfrform.step1.owner-desc-1")}</p>
                    <p className="font-blue">{t("rfr.rfrform.step1.owner-desc-2")}</p>
                    <p className="font-blue">{t("rfr.rfrform.step1.owner-desc-3")}</p>
                    <div className="font-blue rfr-form-row">
                        <div className="rfr-form-row-checkbox-small">
                            <div>
                                <span>
                                    <input id="step11OwnerInput_Yes" name="step11OwnerInput" type="checkbox" onChange={(e) => handleCheckboxInput(e)} value="YES" checked={stepdata["step11OwnerInput"] === "YES" ? true : false} />
                                    <label htmlFor="step11OwnerInput_Yes">{t("rfr.rfrform.YES")}</label>
                                </span>
                            </div>
                        </div>
                        <div className="rfr-form-row-checkbox-small">
                            <div>
                                <span>
                                    <input id="step11OwnerInput_No" name="step11OwnerInput" type="checkbox" onChange={(e) => handleCheckboxInput(e)} value="NO" checked={stepdata["step11OwnerInput"] === "NO" ? true : false} />
                                    <label htmlFor="step11OwnerInput_No">{t("rfr.rfrform.NO")}</label>
                                </span>
                            </div>
                        </div>
                    </div>
                    <AmpFormError error={errors["step11OwnerInput"] !== ""} errorMessage={t("rfr.rfrform.step1.error-owner")} />
                </div>
                {
                    stepdata.step11OwnerInput !== "" ?
                        (
                            stepdata["step11OwnerInput"] === "YES" ?
                                (
                                    <div id="ownerYesDiv" className="rfr-form-cont">
                                    </div>
                                )
                                :
                                (
                                    <div id="ownerNoDiv" className="rfr-form-cont">
                                        <div className="rfr-form-description">
                                            <p className="font-blue">{t("rfr.rfrform.step1.owner-no-desc")}</p>
                                            <div className="font-blue rfr-form-row">
                                                <div className="rfr-form-row-checkbox-small">
                                                    <div>
                                                        <span>
                                                            <input id="step11RepTypeInput_Legal" name="step11RepTypeInput" type="checkbox" onChange={(e) => handleCheckboxInput(e)} value="repLegal" checked={stepdata["step11RepTypeInput"] === "repLegal" ? true : false} />
                                                            <label htmlFor="step11RepTypeInput_Legal">{t("rfr.rfrform.step1.rep-legal")}</label>
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="rfr-form-row-checkbox-small">
                                                    <div>
                                                        <span>
                                                            <input id="step11RepTypeInput_Other" name="step11RepTypeInput" type="checkbox" onChange={(e) => handleCheckboxInput(e)} value="repOther" checked={stepdata["step11RepTypeInput"] === "repOther" ? true : false} />
                                                            <label htmlFor="step11RepTypeInput_Other">{t("rfr.rfrform.step1.rep-other")}</label>
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <AmpFormError error={errors["step11RepTypeInput"] !== ""} errorMessage={t("rfr.rfrform.step1.error-rep")} />
                                        </div>
                                    </div>
                                )
                        )
                        :
                        ("")
                }

                {
                    stepdata.step11RepTypeInput !== "" ?
                        (
                            stepdata["step11RepTypeInput"] === "repLegal" ?
                                (
                                    <div id="repLegalDiv" className="rfr-form-cont">
                                        <div className="rfr-form-description">
                                            <p className="font-blue">
                                                {t("rfr.rfrform.step1.rep-legal-desc-1")}
                                                <em>{t("rfr.rfrform.step1.rep-legal-desc-2")}</em>
                                                {t("rfr.rfrform.step1.rep-legal-desc-3")}
                                            </p>
                                        </div>
                                        <div className="rfr-form-row">
                                            <p>
                                                <span>* {t("rfr.rfrform.required")}</span>
                                            </p>
                                        </div>
                                        <RfrInputName label={t("rfr.rfrform.step1.desc-repname")} name="step11RepNameInput" required={true} placeholderfirst={t("rfr.rfrform.firstname")} placeholderlast={t("rfr.rfrform.lastname")} data={stepdata} onInput={handleInputData} />
                                        <AmpFormError error={errors["step11RepNameInput"] !== ""} errorMessage={t("rfr.rfrform.error-name")} />
                                        <RfrInput label={t("rfr.rfrform.step1.desc-license")} name="step11RepLicenceInput" id="step11RepLicenceInput" type="text" required={true} value={stepdata.step11RepLicenceInput} onInput={handleInputData} />
                                        <AmpFormError error={errors["step11RepLicenceInput"] !== ""} errorMessage={t("rfr.rfrform.step1.error-license")} />
                                        <RfrInputPhone label={t("rfr.rfrform.phonenumber")} name="step11RepPhoneInput" required={true} data={stepdata} onInput={handleInputData} />
                                        <AmpFormError error={errors["step11RepPhoneInput"] !== ""} errorMessage={t("rfr.rfrform.error-phone")} />
                                        <RfrInput label={t("rfr.rfrform.emailaddress")} name="step11RepEmailInput" type="text" required={false} disabled={true} value={formdata["step11RepEmailInput"]} />
                                        <RfrInput label={t("rfr.rfrform.step1.desc-company")} name="step11RepCompanyInput" id="step11RepCompanyInput" type="text" required={false} value={stepdata.step11RepCompanyInput} onInput={handleInputData} />
                                        <RfrInput label={t("rfr.rfrform.step1.desc-position")} name="step11RepPositionInput" id="step11RepPositionInput" type="text" required={false} value={stepdata.step11RepPositionInput} onInput={handleInputData} />
                                    </div>
                                )
                                :
                                (
                                    <div id="repOtherDiv" className="rfr-form-cont">
                                        <div className="rfr-form-description">
                                            <p className="font-blue">
                                                {t("rfr.rfrform.step1.rep-other-desc-1")}
                                                <a href={t("rfr.rfrform.step1.raf")} target="_blank" rel="noopener noreferrer">
                                                    {t("rfr.rfrform.step1.rep-other-desc-2")}
                                                </a>
                                                {t("rfr.rfrform.step1.rep-other-desc-3")}
                                                <Link to="/amp/contact-us" target="_blank" rel="noreferrer noopener">
                                                    {t("rfr.rfrform.step1.rep-other-desc-4")}
                                                </Link>
                                                .
                                            </p>
                                        </div>
                                        <div className="rfr-form-row">
                                            <p>
                                                <span className="required">* {t("rfr.rfrform.required")}</span>
                                            </p>
                                        </div>
                                        <RfrInputName label={t("rfr.rfrform.step1.desc-repname")} name="step11RepOtherNameInput" required={true} placeholderfirst={t("rfr.rfrform.firstname")} placeholderlast={t("rfr.rfrform.lastname")} data={stepdata} onInput={handleInputData} />
                                        <AmpFormError error={errors["step11RepOtherNameInput"] !== ""} errorMessage={t("rfr.rfrform.error-name")} />
                                        <RfrInputPhone label={t("rfr.rfrform.phonenumber")} name="step11RepOtherPhoneInput" required={true} data={stepdata} onInput={handleInputData} />
                                        <AmpFormError error={errors["step11RepOtherPhoneInput"] !== ""} errorMessage={t("rfr.rfrform.error-phone")} />
                                        <RfrInput label={t("rfr.rfrform.emailaddress")} name="step11RepOtherEmailInput" type="text" required={false} disabled={true} value={formdata["step11RepOtherEmailInput"]} />
                                        <RfrInput label={t("rfr.rfrform.step1.desc-company")} name="step11RepOtherCompanyInput" type="text" required={false} value={stepdata.step11RepOtherCompanyInput} onInput={handleInputData} />
                                        <RfrInput label={t("rfr.rfrform.step1.desc-position")} name="step11RepOtherPositionInput" type="text" required={false} value={stepdata.step11RepOtherPositionInput} onInput={handleInputData} />
                                        <RfrUploadFiles name="step11RepOtherFileInput" maxFiles={3} files={files} setFiles={setFiles} allowedTypes=".jpeg, .jpg, .png, .txt, .doc, .docx, .pdf" maxSizeTotal={2 * 1024 * 1024} maxSizeLabelKey="rfr.rfrform.step1.max-file-size" desc={t("rfr.rfrform.step1.desc-files")} />
                                        <AmpFormError error={errors["step11RepOtherFileInput"] !== ""} errorMessage={t("rfr.rfrform.step1.error-files")} />
                                    </div>
                                )
                        )
                        :
                        ("")
                }
            </div>
            <div className="box_footer">
                <div>
                    <Link className="need-help-btn amp-anchor" to="/amp/contact-us" target="_blank" rel="noreferrer noopener">
                        {t("rfr.rfrform.needhelp")}
                    </Link>
                </div>
                <div>
                    <AmpButton onClick={(e) => handleCancel(e)} text={t("rfr.rfrform.step1.cancel")} type="cancel" />
                    <AmpButton onClick={(e) => clearStepdata(e)} text={t("rfr.rfrform.clear")} type="cancel" />
                    <AmpButton onClick={(e) => handleNext(e)} text={t("rfr.rfrform.next")} type="submit" />
                </div>
            </div>
        </div>
    );
}